import {
  AutocompleteWithLabel,
  IOSSwitch,
  SelectWithLabel
} from '@adotel/components'
import {
  LazyQueryExecFunction,
  OperationVariables,
  useLazyQuery,
  useQuery
} from '@apollo/client'
import { ButtonBase, Grow, MenuItem, Typography } from '@material-ui/core'
import React, { useEffect, useState } from 'react'

import { ACCOMMODATIONS_QUERY } from '../../../graphql/accommodation'
import { PROVIDERS_QUERY } from '../../../graphql/providerQueries'
import { RATEPLANS_QUERY } from '../../../graphql/rateplan'
import { ROOMTYPES_QUERY } from '../../../graphql/roomtype'
import {
  getCurrenciesAvailable,
  getRoleFromStorage,
  isFaAvailableInLanguages
} from '../../../helpers/userData'
import { getTranslate } from '../../../localization'
import inventoryPagesRoleManagement from '../../../staticRoleManagement/inventoryRoleManagement'
import { Accommodation } from '../../../types/accommodation'
import { Channel } from '../../../types/channel'
import { Currency } from '../../../types/currency'
import { CRSInventoryData, GetInventoryInput } from '../../../types/inventory'
import { ProviderBrief } from '../../../types/provider'
import { RatePlanBrief } from '../../../types/rateplan'
import { RoomTypeBrief } from '../../../types/roomtype'
import useStyles from './styles/PrimaryFilterModal.styles'

interface FilterModalProps {
  getInventoryInput: GetInventoryInput
  setgetInventoryInput: React.Dispatch<React.SetStateAction<GetInventoryInput>>
  setFilterButtonSelected: React.Dispatch<React.SetStateAction<boolean>>
  autoCompleteLabelAccommodation: any
  setautoCompleteLabelAccommodation: React.Dispatch<any>
  shownTable: 'roomtypes' | 'providers' | 'rateplans'
  setshownTable: React.Dispatch<
    React.SetStateAction<'roomtypes' | 'providers' | 'rateplans'>
  >
  setoccupancy: React.Dispatch<React.SetStateAction<number | 'def'>>
  occupancy: number | 'def'
  sethideZeroes: React.Dispatch<React.SetStateAction<boolean>>
  hideZeroes: boolean
  channels: Channel[]
  loadingForChannel: boolean
  getCRSInventory: LazyQueryExecFunction<
    {
      crsInventory: CRSInventoryData
    },
    OperationVariables
  >
  isRoleChannel: boolean | undefined
  isRoleProvider: boolean | undefined
}

const FilterModal = ({
  setFilterButtonSelected,
  autoCompleteLabelAccommodation,
  setautoCompleteLabelAccommodation,
  shownTable,
  setshownTable,
  setoccupancy,
  occupancy,
  sethideZeroes,
  hideZeroes,
  getInventoryInput,
  setgetInventoryInput,
  channels,
  loadingForChannel,
  getCRSInventory,
  isRoleChannel,
  isRoleProvider
}: FilterModalProps) => {
  const classes = useStyles()
  const [autoCompleteLabelRoomType, setautoCompleteLabelRoomType] =
    useState<any>(null)
  const [autoCompleteLabelRatePlan, setautoCompleteLabelRatePlan] =
    useState<any>(null)
  const [autoCompleteLabelCurrency, setautoCompleteLabelCurrency] =
    useState<Currency>('IRR')
  const [autoCompleteLabelProvider, setautoCompleteLabelProvider] =
    useState<any>(null)
  const [autoCompleteLabelForChannel, setAutoCompleteLabelForChannel] =
    useState<any>(null)
  const { data: providerData, loading: loadingForProvider } = useQuery<{
    providers: ProviderBrief[]
  }>(PROVIDERS_QUERY, {
    notifyOnNetworkStatusChange: true
  })
  const { data: accommodationsData, loading: loadingForAccommodationList } =
    useQuery<{
      accommodations: Accommodation[]
    }>(ACCOMMODATIONS_QUERY, { notifyOnNetworkStatusChange: true })

  const [getRoomTypes, { data: roomtypes, loading: loadingForRoomtypes }] =
    useLazyQuery<{
      roomTypes: RoomTypeBrief[]
    }>(ROOMTYPES_QUERY)
  const [getRatePlans, { data: rateplans, loading: loadingForRatePlans }] =
    useLazyQuery<{
      ratePlans: RatePlanBrief[]
    }>(RATEPLANS_QUERY, {
      notifyOnNetworkStatusChange: true
    })

  useEffect(() => {
    if (autoCompleteLabelRoomType && !!getInventoryInput.roomTypeCode) {
      getRatePlans({
        variables: {
          propertyCode: getInventoryInput.propertyCode,
          roomTypeCode: !!getInventoryInput.roomTypeCode
            ? getInventoryInput.roomTypeCode
            : '',
          showDeletedRatePlans: false,
          showDeactivedRatePlans: false
        }
      })
    }
  }, [autoCompleteLabelRoomType])
  useEffect(() => {
    if (shownTable === 'rateplans' && autoCompleteLabelAccommodation) {
      getRoomTypes({
        variables: {
          propertyCode: autoCompleteLabelAccommodation.code,
          showDeletedRoomTypes: false,
          showDeactivedRoomTypes: false
        }
      })
    }
  }, [shownTable, autoCompleteLabelAccommodation])
  const isChannel = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.IS_CHANNEL.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )
  return (
    <Grow in>
      <div className={classes.root}>
        <div className={classes.selectBoxes}>
          <div className={classes.oneBoxInRow}>
            <div>
              <div className={classes.ButtonsContainer}>
                <ButtonBase
                  onClick={() => {
                    setFilterButtonSelected(false)
                  }}>
                  <Typography
                    className={classes.typographyOfTitle}
                    variant="h3"
                    color="primary">
                    {getTranslate('Cancel')}
                  </Typography>{' '}
                </ButtonBase>
                <div className={classes.titleContainer}>
                  <Typography
                    className={classes.typographyOfTitle}
                    variant="h1">
                    {getTranslate('Filter')}
                  </Typography>
                </div>

                <ButtonBase
                  onClick={() => {
                    getCRSInventory({
                      variables: {
                        input: getInventoryInput
                      }
                    })
                    setFilterButtonSelected(false)
                    setshownTable('roomtypes')
                  }}>
                  <Typography
                    className={classes.typographyOfTitle}
                    variant="h3"
                    color="primary">
                    {getTranslate('Done')}
                  </Typography>{' '}
                </ButtonBase>
              </div>

              <AutocompleteWithLabel
                label={getTranslate('Accommodation')}
                placeholder={getTranslate('Select accommodation')}
                includeInputInList
                onChange={(event, v: any) => {
                  if (v) {
                    setautoCompleteLabelAccommodation({
                      value: v.value,
                      label: v.label,
                      code: v.code
                    })
                  } else {
                    setautoCompleteLabelAccommodation({
                      value: '',
                      label: '',
                      code: ''
                    })
                  }
                  setautoCompleteLabelRoomType({
                    value: '',
                    label: '',
                    code: ''
                  })
                  setoccupancy('def')
                }}
                fullWidth
                getOptionSelected={(option, value) =>
                  option.value === value.value && option.label === value.label
                }
                getOptionLabel={(option) => option.label || ''}
                options={
                  accommodationsData?.accommodations.map((item) => ({
                    label: item.basicDetails.name,
                    value: item.basicDetails.accommodationID || '',
                    code: item.basicDetails.propertyCode
                  })) || []
                }
                value={autoCompleteLabelAccommodation}
                renderOption={(option) => <>{option.label}</>}
                size="small"
                inputSize="small"
                loading={loadingForAccommodationList}
                loadingText={getTranslate('Loading') + '...'}
              />
              <div className={classes.switchContainer}>
                <Typography variant="body1">
                  {getTranslate(
                    'Turn On the switch button if you want to see price and other restrictions'
                  )}
                </Typography>
                <IOSSwitch
                  color="primary"
                  checked={shownTable === 'rateplans'}
                  onChange={(e) => {
                    setshownTable(e.target.checked ? 'rateplans' : 'providers')
                  }}
                />
              </div>
              <AutocompleteWithLabel
                label={getTranslate('Room Type')}
                placeholder={getTranslate('Select Room type')}
                includeInputInList
                onChange={(event, v: any) => {
                  if (v) {
                    setautoCompleteLabelRoomType({
                      value: v.value,
                      label: v.label,
                      code: v.code
                    })

                    setgetInventoryInput({
                      ...getInventoryInput,
                      ratePlanCode: '',
                      roomTypeCode: v.code
                    })
                  } else {
                    setautoCompleteLabelRoomType(null)
                    setgetInventoryInput({
                      ...getInventoryInput,
                      ratePlanCode: '',
                      roomTypeCode: ''
                    })
                  }
                  setautoCompleteLabelRatePlan(null)
                }}
                getOptionSelected={(option, value) =>
                  option.value === value.value && option.label === value.label
                }
                getOptionLabel={(option) => option.label || ''}
                value={autoCompleteLabelRoomType}
                options={
                  roomtypes?.roomTypes.map((roomtype) => ({
                    label: roomtype.name,
                    value: roomtype.id || '',
                    code: roomtype.displayRoomTypeCode
                  })) || []
                }
                renderOption={(option) => <>{option.label}</>}
                size="small"
                fullWidth
                inputSize="small"
                disabled={shownTable !== 'rateplans'}
                loading={loadingForRoomtypes}
                loadingText={getTranslate('Loading') + '...'}
              />
              <AutocompleteWithLabel
                label={getTranslate('Provider')}
                placeholder={getTranslate('Select Provider')}
                includeInputInList
                onChange={(event, v: any) => {
                  if (v) {
                    setautoCompleteLabelProvider({
                      value: v.value,
                      label: v.label,
                      code: v.code
                    })
                    setgetInventoryInput({
                      ...getInventoryInput,
                      providerCode: v.code
                    })
                  } else {
                    setautoCompleteLabelProvider(null)
                    setgetInventoryInput({
                      ...getInventoryInput,
                      providerCode: ''
                    })
                  }
                }}
                getOptionSelected={(option, value) =>
                  option.value === value.value && option.label === value.label
                }
                getOptionLabel={(option) => option.label || ''}
                value={autoCompleteLabelProvider}
                options={
                  providerData?.providers?.map((provider) => ({
                    label: `${provider.name} - (${provider.displayProviderCode})`,
                    value: provider.id,
                    code: provider.providerCode
                  })) || []
                }
                renderOption={(option) => <>{option.label}</>}
                disabled={isRoleProvider}
                size="small"
                inputSize="small"
                loading={loadingForProvider}
                loadingText={getTranslate('Loading') + '...'}
              />
              <AutocompleteWithLabel
                label={getTranslate('Rateplan')}
                placeholder={getTranslate('Select Rateplan')}
                includeInputInList
                onChange={(event, v: any) => {
                  if (v) {
                    setautoCompleteLabelRatePlan({
                      value: v.value,
                      label: v.label,
                      code: v.code
                    })
                    setgetInventoryInput({
                      ...getInventoryInput,
                      ratePlanCode: v.code
                    })
                  } else {
                    setautoCompleteLabelRatePlan(null)
                    setgetInventoryInput({
                      ...getInventoryInput,
                      ratePlanCode: ''
                    })
                  }
                }}
                value={autoCompleteLabelRatePlan}
                getOptionSelected={(option, value) =>
                  option.value === value.value && option.label === value.label
                }
                getOptionLabel={(option) => option.label || ''}
                options={
                  rateplans?.ratePlans?.map((rateplan) => ({
                    label: rateplan.name,
                    value: rateplan.id || '',
                    code: rateplan.displayRatePlanCode
                  })) || []
                }
                renderOption={(option) => <>{option.label}</>}
                size="small"
                inputSize="small"
                disabled={shownTable !== 'rateplans'}
                loading={loadingForRatePlans}
                loadingText={getTranslate('Loading') + '...'}
              />
              {!isChannel && (
                <AutocompleteWithLabel
                  label={getTranslate('Channel')}
                  placeholder={getTranslate('Select Channel')}
                  value={autoCompleteLabelForChannel}
                  includeInputInList
                  onChange={(_, v: any) => {
                    if (v) {
                      setAutoCompleteLabelForChannel({
                        value: v.value,
                        label: v.label,
                        code: v.code
                      })
                      setgetInventoryInput({
                        ...getInventoryInput,
                        channelID: v.value
                      })
                    } else {
                      setAutoCompleteLabelForChannel(null)
                      setgetInventoryInput({
                        ...getInventoryInput,
                        channelID: ''
                      })
                    }
                  }}
                  getOptionSelected={(option, value) =>
                    option.value === value.value && option.label === value.label
                  }
                  getOptionLabel={(option) => option.label || ''}
                  options={
                    channels
                      ? channels?.map((item) => ({
                          label: item.basicDetails.name,
                          value: item.basicDetails.channelID || '',
                          code: item.basicDetails.displayChannelCode
                        }))
                      : []
                  }
                  renderOption={(option) => <>{option.label}</>}
                  size="small"
                  inputSize="small"
                  loading={loadingForChannel}
                  loadingText={getTranslate('Please wait...')}
                  disabled={isRoleChannel}
                />
              )}
              <SelectWithLabel
                fullWidth
                value={autoCompleteLabelCurrency}
                onChange={(event) => {
                  setautoCompleteLabelCurrency(event.target.value as Currency)
                  setgetInventoryInput({
                    ...getInventoryInput,
                    Currency: event.target.value as Currency
                  })
                }}
                size="small"
                label={getTranslate('Currency')}>
                {getCurrenciesAvailable().map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </SelectWithLabel>
              <SelectWithLabel
                fullWidth
                value={occupancy}
                onChange={(event) => setoccupancy(parseInt(event.target.value))}
                size="small"
                disabled={shownTable !== 'rateplans'}
                label={getTranslate('Occupancy')}>
                <MenuItem value={'def'}>{getTranslate('def')}</MenuItem>
                {Array(7)
                  .fill('')
                  .map((_, index) => (
                    <MenuItem value={index + 1} key={index.toString()}>
                      {index + 1}
                    </MenuItem>
                  ))}
              </SelectWithLabel>
              {isFaAvailableInLanguages() && (
                <div className={classes.hideZerosFromPriceContainer}>
                  <Typography>
                    {getTranslate('Hide 0.000 From Prices')}
                  </Typography>
                  <IOSSwitch
                    color="primary"
                    checked={hideZeroes}
                    onChange={(e) => {
                      sethideZeroes(e.target.checked)
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Grow>
  )
}

export default FilterModal
